import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../Styles/AdaBiddaNidhiTable.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { aadabiddaNidhiAPI } from "../../api/index";
import {
  FaUserFriends,
  FaCheckCircle,
  FaHourglassHalf,
  FaRupeeSign,
  FaSearch,
  FaMapMarkerAlt,
  FaFilter,
  FaSyncAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaEdit,
  FaTrashAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaHandHoldingHeart,
  FaUpload,
  FaDownload,
  FaImage,
} from "react-icons/fa";

const MANDAL_NAMES = [
  "Kandukur",
  "Lingasamudram",
  "Gudluru",
  "Ulavapadu",
  "Voletivaripalem",
];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

// ---------- Excel template settings ----------
// Headings follow the Add Beneficiary form fields (same order as the form).
// CONFIRM these with the backend's bulk-upload parser. If it expects different
// heading names, change them here only.
const TEMPLATE_HEADERS = [
  "Beneficiary Name",
  "Relation",
  "Voter ID",
  "Age",
  "Aadhaar Number",
  "Mobile Number",
  "Bank Account Number",
  "IFSC Code",
  "Monthly Amount (₹)",
  "Mandal",
  "Village",
  "Date",
  "Status",
  "Photo URL",
  "Supporting Document URL",
  "Remarks",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 28 }, // Beneficiary Name
  { wch: 24 }, // Relation
  { wch: 16 }, // Voter ID
  { wch: 8 },  // Age
  { wch: 18 }, // Aadhaar Number
  { wch: 16 }, // Mobile Number
  { wch: 24 }, // Bank Account Number
  { wch: 14 }, // IFSC Code
  { wch: 20 }, // Monthly Amount
  { wch: 20 }, // Mandal
  { wch: 22 }, // Village
  { wch: 14 }, // Date
  { wch: 14 }, // Status
  { wch: 38 }, // Photo URL
  { wch: 38 }, // Supporting Document URL
  { wch: 40 }, // Remarks
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Beneficiary Name", "Yes", "3-100 characters. Alphabets, spaces, '.' and ''' only"],
  ["Relation", "Yes", "2-100 characters (e.g. D/O Ramesh, W/O Suresh)"],
  ["Voter ID", "Yes", "3 capital letters + 7 digits (e.g. ABC1234567)"],
  ["Age", "Yes", "Whole number between 18 and 59"],
  ["Aadhaar Number", "Yes", "Exactly 12 digits, cannot start with 0 or 1. Format the cell as Text in Excel"],
  ["Mobile Number", "Yes", "Exactly 10 digits, must start with 6, 7, 8 or 9. Format the cell as Text in Excel"],
  ["Bank Account Number", "Yes", "Digits only, 9-18 digits. Format the cell as Text in Excel"],
  ["IFSC Code", "Yes", "4 capital letters + 0 + 6 letters/digits (e.g. SBIN0001234)"],
  ["Monthly Amount (₹)", "Yes", "Fixed amount: 1500"],
  ["Mandal", "Yes", MANDAL_NAMES.join(", ")],
  ["Village", "Yes", "Alphabets and spaces only. Must exactly match an existing village under the Mandal"],
  ["Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Status", "Yes", STATUS_OPTIONS.join(", ")],
  ["Photo URL", "No", "Optional. A link starting with http:// or https://"],
  ["Supporting Document URL", "No", "Optional. A link starting with http:// or https://"],
  ["Remarks", "No", "Optional. If filled: 5-500 characters"],
];

const FALLBACK_IMG =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23e5e7eb'/%3E%3C/svg%3E";

const num = (v) => (typeof v === "number" ? v : Number(v) || 0);

const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || d?.data || [];
};

const extractTotalCount = (res, fallbackLen) => {
  const d = res?.data;
  return num(d?.total ?? d?.count ?? d?.total_count ?? fallbackLen);
};

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

const normalizeRow = (row) => ({
  id: row.id,
  name: row.beneficiary_name || "Untitled",
  relation: row.relation_name || "",
  voterId: row.epic_no || "",
  age: row.age ?? "",
  aadhaarMasked: row.aadhaar_masked || "",
  mobile: row.mobile_number || "",
  bankAccount: row.bank_account_number || "",
  ifsc: row.ifsc_code || "",
  amount: num(row.amount),
  mandalId: row.mandal_id,
  mandal: row.mandal_name || (row.mandal_id ? `Mandal #${row.mandal_id}` : "—"),
  villageId: row.village_id,
  village: row.village_name || (row.village_id ? `Village #${row.village_id}` : "—"),
  date: row.application_date || "",
  status: row.status || "pending",
  photo: row.photo_url || "",
  document: row.document_url || "",
  remarks: row.remarks || "",
});

const AdaBiddaNidhi = () => {
  const navigate = useNavigate();

  const [adbddanidhiData, setAdbddanidhiData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 10;

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- IMAGE PREVIEW (in-page popup) ----------
  const [previewItem, setPreviewItem] = useState(null);
  const openPreview = (item) => setPreviewItem(item);
  const closePreview = () => setPreviewItem(null);

  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: PAGE_SIZE };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (mandalFilter !== "All Mandals") params.mandal_name = mandalFilter;
      if (statusFilter !== "All Status") params.status = statusFilter;

      const res = await aadabiddaNidhiAPI.getAll(params);
      const rows = extractList(res).map(normalizeRow);
      setAdbddanidhiData(rows);
      setTotalCount(extractTotalCount(res, rows.length));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, mandalFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close image preview with Escape key
  useEffect(() => {
    if (!previewItem) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [previewItem]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + adbddanidhiData.length;

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const approvedCount = adbddanidhiData.filter((i) => i.status === "approved").length;
  const pendingCount = adbddanidhiData.filter((i) => i.status === "pending").length;
  const totalAmount = adbddanidhiData.reduce((sum, i) => sum + i.amount, 0);

  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || "",
      relation: item.relation || "",
      voterId: item.voterId || "",
      age: item.age ?? "",
      aadhaar: "",
      mobile: item.mobile || "",
      bankAccount: item.bankAccount || "",
      ifsc: item.ifsc || "",
      amount: item.amount ?? "",
      mandalName: item.mandal || "",
      villageName: item.village && item.village !== "—" ? item.village : "",
      date: item.date || "",
      status: item.status || "pending",
      photoUrl: item.photo || "",
      documentUrl: item.document || "",
      remarks: item.remarks || "",
    });
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();

    const trimmedVillage = editForm.villageName.trim();
    const trimmedAadhaar = editForm.aadhaar.trim();

    if (!editForm.name.trim()) {
      toast.error("Beneficiary name is required");
      return;
    }
    if (!trimmedVillage) {
      toast.error("Please enter the village name to save");
      return;
    }
    if (!editForm.mandalName) {
      toast.error("Please select a Mandal");
      return;
    }
    if (!trimmedAadhaar) {
      toast.error("Please re-enter the 12-digit Aadhaar Number to save changes");
      return;
    }
    if (trimmedAadhaar.length !== 12 || !/^\d{12}$/.test(trimmedAadhaar)) {
      toast.error("Aadhaar Number must be exactly 12 digits");
      return;
    }
    if (editForm.age && (Number(editForm.age) < 18 || Number(editForm.age) > 59)) {
      toast.error("Age must be between 18 and 59");
      return;
    }
    if (editForm.mobile && editForm.mobile.length !== 10) {
      toast.error("Mobile Number must be exactly 10 digits");
      return;
    }
    if (editForm.photoUrl.trim() && !isLikelyUrl(editForm.photoUrl.trim())) {
      toast.error("Enter a valid Photo URL starting with https://");
      return;
    }
    if (editForm.documentUrl.trim() && !isLikelyUrl(editForm.documentUrl.trim())) {
      toast.error("Enter a valid Document URL starting with https://");
      return;
    }

    const payload = {
      beneficiary_name: editForm.name.trim(),
      relation_name: editForm.relation.trim() || undefined,
      epic_no: editForm.voterId.trim() || undefined,
      age: editForm.age ? Number(editForm.age) : undefined,
      aadhaar_number: trimmedAadhaar,
      mobile_number: editForm.mobile.trim() || undefined,
      bank_account_number: editForm.bankAccount.trim() || undefined,
      ifsc_code: editForm.ifsc.trim() || undefined,
      amount: editForm.amount ? Number(editForm.amount) : undefined,
      mandal_name: editForm.mandalName,
      village_name: trimmedVillage,
      application_date: editForm.date || undefined,
      status: editForm.status,
      photo_url: editForm.photoUrl.trim() || undefined,
      document_url: editForm.documentUrl.trim() || undefined,
      remarks: editForm.remarks.trim() || undefined,
    };

    setSaving(true);
    try {
      await aadabiddaNidhiAPI.update(editItem.id, payload);
      toast.success("Beneficiary updated");
      closeEditModal();
      fetchData();
    } catch (err) {
      console.error("Aadabidda Nidhi update payload sent:", payload);
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (item) => {
    setDeleteItem(item);
  };

  const closeDeleteModal = () => {
    setDeleteItem(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await aadabiddaNidhiAPI.delete(deleteItem.id);
      toast.success("Deleted successfully");
      closeDeleteModal();
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  /* ---------- Download Excel template (headings only) ---------- */
  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 26 }, { wch: 10 }, { wch: 90 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "Aadabidda_Nidhi_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  const handleBulkUploadClick = () => bulkFileInputRef.current?.click();

  const handleBulkFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    setBulkUploading(true);
    try {
      const res = await aadabiddaNidhiAPI.bulkUpload(file);
      toast.success(`Import complete — ${num(res.data?.inserted)} beneficiaries added`);
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="ab-page">
      <input
        ref={bulkFileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleBulkFileSelected}
      />

      <div className="ab-header">
        <div className="ab-header-left">
          <div className="ab-header-icon">
            <FaHandHoldingHeart />
          </div>
          <div>
            <h2 className="ab-title">Aadabidda Nidhi Beneficiaries</h2>
            <p className="ab-subtitle">
              View and manage beneficiaries under Aadabidda Nidhi scheme
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="ab-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <span className="ab-add-plus">
              <FaDownload />
            </span>{" "}
            Download Template
          </button>

          <button
            className="ab-add-btn"
            onClick={handleBulkUploadClick}
            disabled={bulkUploading}
            style={{ background: "#0b3d24" }}
          >
            <span className="ab-add-plus">
              {bulkUploading ? "⏳" : <FaUpload />}
            </span>{" "}
            {bulkUploading ? "Uploading..." : "Bulk Upload (Excel)"}
          </button>

          <button
            className="ab-add-btn"
            onClick={() => navigate("/dashboard/AdaBiddaNidhiForm")}
          >
            <span className="ab-add-plus">+</span> Add New Beneficiary
          </button>
        </div>
      </div>

      <div className="ab-scroll-body">
        <div className="ab-stats-row">
          <div className="ab-stat-card">
            <div className="ab-stat-icon ab-icon-pink">
              <FaUserFriends />
            </div>
            <div>
              <p className="ab-stat-label">Total Beneficiaries</p>
              <h3 className="ab-stat-value">{totalCount}</h3>
              <p className="ab-stat-sub">All time records</p>
            </div>
          </div>

          <div className="ab-stat-card">
            <div className="ab-stat-icon ab-icon-green">
              <FaCheckCircle />
            </div>
            <div>
              <p className="ab-stat-label">Approved Beneficiaries</p>
              <h3 className="ab-stat-value">{approvedCount}</h3>
              <p className="ab-stat-sub ab-sub-green">
                {adbddanidhiData.length ? ((approvedCount / adbddanidhiData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="ab-stat-card">
            <div className="ab-stat-icon ab-icon-orange">
              <FaHourglassHalf />
            </div>
            <div>
              <p className="ab-stat-label">Pending Beneficiaries</p>
              <h3 className="ab-stat-value">{pendingCount}</h3>
              <p className="ab-stat-sub ab-sub-orange">
                {adbddanidhiData.length ? ((pendingCount / adbddanidhiData.length) * 100).toFixed(1) : 0}% of page
              </p>
            </div>
          </div>

          <div className="ab-stat-card">
            <div className="ab-stat-icon ab-icon-purple">
              <FaRupeeSign />
            </div>
            <div>
              <p className="ab-stat-label">Total Amount Disbursed</p>
              <h3 className="ab-stat-value">₹{totalAmount.toLocaleString("en-IN")}</h3>
              <p className="ab-stat-sub ab-sub-purple">This page</p>
            </div>
          </div>
        </div>

        <div className="ab-filter-section">
          <div className="ab-search-wrapper">
            <FaSearch className="ab-search-icon" />
            <input
              type="text"
              placeholder="Search Beneficiary..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="ab-select-wrapper">
            <FaMapMarkerAlt className="ab-select-icon" />
            <select
              value={mandalFilter}
              onChange={(e) => {
                setMandalFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Mandals</option>
              {MANDAL_NAMES.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="ab-select-wrapper">
            <FaFilter className="ab-select-icon" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All Status">All Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>

          <button className="ab-reset-btn" onClick={handleResetFilters}>
            <FaSyncAlt /> Reset Filters
          </button>
        </div>

        <div className="ab-table-wrapper">
          <table className="ab-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Photo</th>
                <th>Name</th>
                <th>Relation</th>
                <th>Amount</th>
                <th>Village</th>
                <th>Mandal</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="ab-no-data">
                    Loading…
                  </td>
                </tr>
              ) : adbddanidhiData.length > 0 ? (
                adbddanidhiData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>

                    <td>
                      {item.photo ? (
                        <button
                          type="button"
                          className="ab-thumb-btn"
                          onClick={() => openPreview(item)}
                          title="View photo"
                          style={{
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={item.photo}
                            alt={item.name}
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "6px",
                              objectFit: "cover",
                              border: "1px solid #e2e8f0",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = FALLBACK_IMG;
                            }}
                          />
                        </button>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "42px",
                            height: "42px",
                            borderRadius: "6px",
                            background: "#f1f5f9",
                            color: "#9ca3af",
                          }}
                          title="No photo available"
                        >
                          <FaImage size={16} />
                        </span>
                      )}
                    </td>

                    <td>
                      <div className="ab-name-cell">
                        <span className="ab-avatar">
                          <FaUserCircle />
                        </span>
                        <span className="ab-name-text">{item.name}</span>
                      </div>
                    </td>
                    <td className="ab-relation-cell">{item.relation}</td>
                    <td className="ab-amount">₹{item.amount.toLocaleString("en-IN")}</td>
                    <td>
                      <span className="ab-village-cell">
                        <FaMapMarkerAlt /> {item.village}
                      </span>
                    </td>
                    <td>{item.mandal}</td>
                    <td>
                      <span className="ab-date-cell">
                        <FaCalendarAlt /> {item.date}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`ab-status ${
                          item.status === "approved" ? "ab-approved" : "ab-pending"
                        }`}
                      >
                        {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </td>
                    <td className="ab-action-cell">
                      <button
                        className="ab-edit-btn"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="ab-delete-btn"
                        onClick={() => openDeleteModal(item)}
                      >
                        <FaTrashAlt /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="ab-no-data">
                    No matching beneficiaries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="ab-footer">
          <p className="ab-footer-text">
            {totalCount > 0
              ? `Showing ${startIndex + 1} to ${endIndex} of ${totalCount} beneficiaries`
              : "Showing 0 beneficiaries"}
          </p>

          <div className="ab-pagination">
            <button
              className="ab-page-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaChevronLeft />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`ab-page-btn ${
                  currentPage === page ? "ab-page-active" : ""
                }`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="ab-page-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="ab-scheme-banner">
          <div className="ab-banner-icon-left">
            <FaHandHoldingHeart />
          </div>

          <div className="ab-banner-text">
            <h4>Aadabidda Nidhi Scheme</h4>
            <p>
              A scheme to support the education and bright future of every
              girl child in Andhra Pradesh.
            </p>
          </div>

          <div className="ab-banner-icon-right">
            <FaHandHoldingHeart />
          </div>
        </div>
      </div>

      {/* ---------------- IMAGE PREVIEW POPUP ---------------- */}
      {previewItem && (
        <div
          className="ab-modal-overlay"
          onClick={closePreview}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            className="ab-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "480px",
              width: "100%",
              background: "#fff",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <div
              className="ab-modal-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 18px",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "16px" }}>{previewItem.name}</h3>
              <button
                className="ab-modal-close"
                onClick={closePreview}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#475569",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: "18px", textAlign: "center" }}>
              {previewItem.photo ? (
                <img
                  src={previewItem.photo}
                  alt={previewItem.name}
                  style={{
                    width: "100%",
                    maxHeight: "60vh",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = FALLBACK_IMG;
                  }}
                />
              ) : (
                <div className="ab-no-data">No photo available.</div>
              )}
            </div>

            <div
              style={{
                padding: "0 18px 18px",
                fontSize: "13px",
                color: "#475569",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <span>
                <strong>Village:</strong> {previewItem.village} &nbsp;•&nbsp;{" "}
                <strong>Mandal:</strong> {previewItem.mandal}
              </span>
              <span>
                <strong>Status:</strong>{" "}
                {STATUS_LABEL[previewItem.status] || previewItem.status}
              </span>
            </div>
          </div>
        </div>
      )}

      {editItem && editForm && (
        <div className="ab-modal-overlay" onClick={closeEditModal}>
          <div
            className="ab-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ab-modal-header">
              <h3>Edit Beneficiary</h3>
              <button className="ab-modal-close" onClick={closeEditModal}>
                <FaTimes />
              </button>
            </div>

            <form className="ab-modal-form" onSubmit={handleEditSave}>
              <div className="ab-form-grid">
                <div className="ab-form-row">
                  <label>Beneficiary Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => handleEditChange("name", e.target.value)}
                    required
                  />
                </div>

                <div className="ab-form-row">
                  <label>Relation</label>
                  <input
                    type="text"
                    value={editForm.relation}
                    onChange={(e) => handleEditChange("relation", e.target.value)}
                  />
                </div>

                <div className="ab-form-row">
                  <label>Voter ID (EPIC No.)</label>
                  <input
                    type="text"
                    value={editForm.voterId}
                    onChange={(e) => handleEditChange("voterId", e.target.value)}
                  />
                </div>

                <div className="ab-form-row">
                  <label>Age</label>
                  <input
                    type="number"
                    min="18"
                    max="59"
                    value={editForm.age}
                    onChange={(e) => handleEditChange("age", e.target.value)}
                  />
                </div>

                <div className="ab-form-row">
                  <label>Aadhaar Number * (re-enter to save)</label>
                  <input
                    type="text"
                    maxLength="12"
                    placeholder="Enter 12-digit Aadhaar Number"
                    value={editForm.aadhaar}
                    onChange={(e) => handleEditChange("aadhaar", e.target.value)}
                    required
                  />
                  <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    On file: {editItem.aadhaarMasked || "—"}. The full number must be re-entered here every time — the server does not accept updates without it.
                  </small>
                </div>

                <div className="ab-form-row">
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    maxLength="10"
                    value={editForm.mobile}
                    onChange={(e) => handleEditChange("mobile", e.target.value)}
                  />
                </div>

                <div className="ab-form-row">
                  <label>Bank Account Number</label>
                  <input
                    type="text"
                    value={editForm.bankAccount}
                    onChange={(e) => handleEditChange("bankAccount", e.target.value)}
                  />
                </div>

                <div className="ab-form-row">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    value={editForm.ifsc}
                    onChange={(e) => handleEditChange("ifsc", e.target.value)}
                  />
                </div>

                <div className="ab-form-row">
                  <label>Amount (₹)</label>
                  <input
                    type="number"
                    value={editForm.amount}
                    onChange={(e) => handleEditChange("amount", e.target.value)}
                    required
                  />
                </div>

                <div className="ab-form-row">
                  <label>Village *</label>
                  <input
                    type="text"
                    placeholder="Enter Village Name"
                    value={editForm.villageName}
                    onChange={(e) => handleEditChange("villageName", e.target.value)}
                    required
                  />
                  <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    Must exactly match an existing village name under the selected Mandal.
                  </small>
                </div>

                <div className="ab-form-row">
                  <label>Mandal *</label>
                  <select
                    value={editForm.mandalName}
                    onChange={(e) => handleEditChange("mandalName", e.target.value)}
                  >
                    {MANDAL_NAMES.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div className="ab-form-row">
                  <label>Date</label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => handleEditChange("date", e.target.value)}
                    required
                  />
                </div>

                <div className="ab-form-row">
                  <label>Status *</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => handleEditChange("status", e.target.value)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>

                <div className="ab-form-row">
                  <label>Photo URL</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editForm.photoUrl}
                    onChange={(e) => handleEditChange("photoUrl", e.target.value)}
                  />
                </div>

                <div className="ab-form-row">
                  <label>Supporting Document URL</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editForm.documentUrl}
                    onChange={(e) => handleEditChange("documentUrl", e.target.value)}
                  />
                </div>
              </div>

              {editForm.photoUrl && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={editForm.photoUrl}
                    alt="Beneficiary preview"
                    style={{
                      width: "110px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              <div className="ab-form-row ab-full-width" style={{ marginTop: "14px" }}>
                <label>Remarks</label>
                <textarea
                  rows="3"
                  value={editForm.remarks}
                  onChange={(e) => handleEditChange("remarks", e.target.value)}
                />
              </div>

              <div className="ab-modal-actions">
                <button
                  type="button"
                  className="ab-modal-cancel-btn"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="ab-modal-save-btn" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteItem && (
        <div className="ab-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="ab-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ab-confirm-icon">
              <FaTrashAlt />
            </div>
            <h3 className="ab-confirm-title">Delete this beneficiary?</h3>
            <p className="ab-confirm-text">
              Are you sure you want to delete{" "}
              <strong>{deleteItem.name}</strong> from the beneficiary list?
              This action cannot be undone.
            </p>

            <div className="ab-confirm-actions">
              <button
                className="ab-confirm-cancel-btn"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                className="ab-confirm-delete-btn"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdaBiddaNidhi;